class a extends Error {
  constructor(r, n) {
    super(r), this.operation = n, this.name = "EncryptionError";
  }
  operation;
}
class c extends a {
  constructor(r) {
    super("Data corruption or tampering detected", r), this.name = "TamperingError";
  }
}
class y extends a {
  constructor(r) {
    super(r, "key-generation"), this.name = "KeyGenerationError";
  }
}
class p {
  secretKey;
  ivLength;
  onError;
  algorithm;
  maxRetries;
  constructor({
    secretKey: r,
    ivLength: n = 12,
    onError: e = (t) => console.error(t),
    algorithm: i = "AES-GCM",
    maxRetries: o = 0
  }) {
    if (!r)
      throw new a("A secret key is required", "key-generation");
    this.secretKey = r, this.ivLength = n, this.onError = e, this.algorithm = i, this.maxRetries = o;
  }
  // Generate a crypto key from the secret key
  async getCryptoKey() {
    const n = new TextEncoder().encode(this.secretKey), e = new Uint8Array(32).map(
      (i, o) => n[o % n.length]
      // Repeat key bytes if it's too short
    );
    try {
      return await crypto.subtle.importKey(
        "raw",
        e,
        {
          name: this.algorithm,
          ...this.algorithm === "AES-GCM" ? { length: 256 } : {}
        },
        !1,
        ["encrypt", "decrypt"]
      );
    } catch (i) {
      throw this.onError(
        new y(
          `Crypto key generation failed: ${i instanceof Error ? i.message : "Unknown error"}`
        )
      ), new y(
        "Crypto key generation failed. Ensure the key is 128 or 256 bits."
      );
    }
  }
  async retryOperation(r) {
    let n;
    for (let e = 0; e <= this.maxRetries; e++)
      try {
        return await r();
      } catch (i) {
        if (n = i, e < this.maxRetries) {
          await new Promise(
            (o) => setTimeout(o, Math.pow(2, e) * 100)
          );
          continue;
        }
        break;
      }
    throw n;
  }
  /**
   * Encrypts the provided data using the configured algorithm.
   * @param data The data to encrypt
   * @returns A base64 encoded string containing the IV and encrypted data
   * @throws EncryptionError if encryption fails
   */
  async encrypt(r) {
    return this.retryOperation(async () => {
      const n = await this.getCryptoKey(), e = crypto.getRandomValues(new Uint8Array(this.ivLength)), i = new TextEncoder().encode(JSON.stringify(r));
      try {
        const o = await crypto.subtle.encrypt(
          {
            name: this.algorithm,
            iv: e
          },
          n,
          i
        ), t = new Uint8Array(
          e.byteLength + o.byteLength
        );
        return t.set(e), t.set(new Uint8Array(o), e.byteLength), btoa(String.fromCharCode(...t));
      } catch (o) {
        throw this.onError(
          new a(
            `Encryption failed: ${o instanceof Error ? o.message : "Unknown error"}`,
            "encrypt"
          )
        ), new a("Data encryption failed", "encrypt");
      }
    });
  }
  /**
   * Decrypts the provided encrypted data.
   * @param encryptedData The base64 encoded encrypted data
   * @returns The decrypted data of type T
   * @throws EncryptionError if decryption fails
   * @throws TamperingError if data corruption or tampering is detected
   */
  async decrypt(r) {
    return this.retryOperation(async () => {
      const n = await this.getCryptoKey(), e = new Uint8Array(
        atob(r).split("").map((t) => t.charCodeAt(0))
      ), i = e.slice(0, this.ivLength), o = e.slice(this.ivLength);
      try {
        const t = await crypto.subtle.decrypt(
          {
            name: this.algorithm,
            iv: i
          },
          n,
          o
        ), h = new TextDecoder().decode(t);
        return JSON.parse(h);
      } catch (t) {
        throw t instanceof DOMException && t.name === "OperationError" ? (this.onError(new c("decrypt")), new c("decrypt")) : (this.onError(
          new a(
            `Decryption failed: ${t instanceof Error ? t.message : "Unknown error"}`,
            "decrypt"
          )
        ), new a(
          "An unexpected error occurred during decryption",
          "decrypt"
        ));
      }
    });
  }
}
export {
  a as EncryptionError,
  p as EncryptionService,
  y as KeyGenerationError,
  c as TamperingError
};
